import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Slider from './Slider';
import CustomInput from '../Input';
import { 
  sliderValueToPercentage, 
  percentageToSliderValue,
  redistributeWeightages,
  validatePercentageInput,
  getWeightageSummary
} from '../../utils/weightageUtils';
import { viewSizeCalculator } from "../../utils";

const WeightageInput = ({
  label,
  sliderValue,
  percentageValue,
  onSliderChange,
  onPercentageChange,
  onWeightageChange,
  isActive = true,
  totalWeightages,
  currentKey
}) => {
  const [localPercentage, setLocalPercentage] = useState(percentageValue || 0);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');

  // Update local percentage when prop changes
  useEffect(() => {
    // Format to 2 decimal places when not editing
    if (!isEditing) {
      setLocalPercentage((percentageValue || 0).toFixed(2));
    }
  }, [percentageValue, isEditing]);

  const handleSliderChange = (newSliderValue) => {
    const newPercentage = sliderValueToPercentage(newSliderValue);
    
    // Redistribute weightages to maintain 100% total
    const redistributedWeightages = redistributeWeightages(
      totalWeightages, 
      currentKey, 
      newPercentage
    );
    
    onWeightageChange(redistributedWeightages);
  };

  const handlePercentageInputChange = (name, value) => {
    setLocalPercentage(value);
    setError('');
    
    // Allow real-time updates while typing
    const validation = validatePercentageInput(value);
    if (validation.isValid) {
      const newPercentage = validation.value;
      
      // Only redistribute if we have a valid number (not empty)
      if (value !== '') {
        const redistributedWeightages = redistributeWeightages(
          totalWeightages, 
          currentKey, 
          newPercentage
        );
        
        onWeightageChange(redistributedWeightages);
      }
    }
  };

  const handlePercentageInputBlur = () => {
    setIsEditing(false);
    
    // If input is empty, set it to 0
    if (localPercentage === '') {
      setLocalPercentage('0.00');
      const redistributedWeightages = redistributeWeightages(
        totalWeightages, 
        currentKey, 
        0
      );
      onWeightageChange(redistributedWeightages);
      return;
    }
    
    const validation = validatePercentageInput(localPercentage);
    if (!validation.isValid) {
      setError(validation.error);
      setLocalPercentage((percentageValue || 0).toFixed(2));
      return;
    }
    
    // Format to 2 decimal places on blur
    setLocalPercentage(validation.value.toFixed(2));
  };

  const handlePercentageInputFocus = () => {
    setIsEditing(true);
    setError('');
  };

  const handlePercentageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handlePercentageInputBlur();
    }
  };

  // Calculate slider value from percentage
  const currentSliderValue = percentageToSliderValue(percentageValue || 0);

  // Get weightage summary for display
  const summary = getWeightageSummary(totalWeightages);

  return (
    <WeightageInputContainer>
      <SliderContainer>
        <Slider
          label={label}
          rating={currentSliderValue}
          onChange={handleSliderChange}
        />
      </SliderContainer>
      
      <InputsContainer>
        <WeightageInputWrapper>
          <CustomInput
            placeholder="Weightage"
            dropdown={false}
            label="Weightage"
            name={`slider-${label}`}
            value={currentSliderValue}
            onChange={(name, value) => {
              const numValue = parseInt(value) || 0;
              if (numValue >= 0 && numValue <= 10) {
                handleSliderChange(numValue);
              }
            }}
            error={false}
            showError={false}
            disabled={!isActive}
          />
        </WeightageInputWrapper>
        
                 <PercentageInputWrapper>
           <CustomInput
             placeholder="%"
             dropdown={false}
             label="Percentage"
             name={`percentage-${label}`}
             value={localPercentage}
             onChange={handlePercentageInputChange}
             onBlur={handlePercentageInputBlur}
             onFocus={handlePercentageInputFocus}
             onKeyDown={handlePercentageInputKeyDown}
             error={!!error}
             showError={!!error}
             errorMessage={error}
             disabled={!isActive}
           />
         </PercentageInputWrapper>
      </InputsContainer>
             {summary.total.toFixed(2) > 100 && (
         <WarningMessage>
           Total exceeds 100% ({summary.total.toFixed(2)}%)
         </WarningMessage>
       )}
       
       {summary.total < 100 && summary.activeCount > 0 && (
         <InfoMessage>
           Total: {summary.total.toFixed(2)}% (distribute remaining {Math.abs(100 - summary.total).toFixed(2)}%)
         </InfoMessage>
       )}
    </WeightageInputContainer>
  );
};

export default WeightageInput;

const WeightageInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${() => `${viewSizeCalculator(1)}`};
  width: 100%;
`;

const SliderContainer = styled.div`
  width: 100%;
`;

const InputsContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
`;

const WeightageInputWrapper = styled.div`
  flex: 1;
`;

const PercentageInputWrapper = styled.div`
  flex: 1;
`;

const WarningMessage = styled.div`
  color: #dc3545;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 8px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
`;

const InfoMessage = styled.div`
  color: #17a2b8;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 8px;
  background-color: #d1ecf1;
  border: 1px solid #bee5eb;
  border-radius: 4px;
`;
